<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>DNS info</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-4">
                    <FormField v-slot="{ componentField }" name="q">
                        <FormItem>
                            <FormLabel>Domain / IP / ASN (Required)</FormLabel>
                            <FormControl>
                                <Text v-bind="componentField" placeholder="Search" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField v-slot="{ componentField }" name="nameserver">
                        <FormItem>
                            <FormLabel>Nameserver (Required)</FormLabel>
                            <FormControl>
                                <Text v-bind="componentField" placeholder="Enter nameserver" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="searching" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="searching"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Search</Button
                    >
                </DialogFooter>
            </form>
            <DNSInfoResultsModal
                v-model="openResultsModal"
                :dns="dns"
                :nameserver="nameserver"
                :loading="searching"
                @refresh="onSubmit"
            />
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import Text from '@/components/ui/input/Text.vue'
import { useToast } from '@/components/ui/toast'
import { defaultGraphQLClient } from '@/composables/default-urql-client'
import { toTypedSchema } from '@vee-validate/zod'
import gql from 'graphql-tag'
import { useForm } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'
import * as z from 'zod'
import DNSInfoResultsModal from './DNSInfoResultsModal.vue'

const open = defineModel()
const openResultsModal = ref(false)
const dns = ref()

const emit = defineEmits(['success'])

const { toast } = useToast()
const searching = ref(false)
const nameserver = ref('')

const schema = z.object({
    q: z.string().min(1, {
        message: "You can't leave this empty.",
    }),
    nameserver: z.string().ip().min(1, {
        message: "You can't leave this empty.",
    }),
})

type Form = z.infer<typeof schema>

const { handleSubmit, meta, setFieldError, setFieldValue } = useForm({
    validationSchema: toTypedSchema(schema),
    initialValues: {
        q: '',
        nameserver: '1.1.1.1',
    },
})

watch(open, (value) => {
    if (value) {
        setFieldValue('q', '')
        setFieldValue('nameserver', '1.1.1.1')
    }
})

const onSubmit = handleSubmit((values) => {
    nameserver.value = values.nameserver
    search(values)
})

function close() {
    open.value = false
}

async function search(values: Form) {
    searching.value = true

    try {
        const { data, error } = await defaultGraphQLClient.query(
            gql`
                query getDNSInfo($q: String!, $nameserver: String!) {
                    dnsInfo(q: $q, nameserver: $nameserver) {
                        domain_name
                        whois_server
                        name_servers
                        creation_date_format_b
                        expiration_date_format_b
                        updated_date_format_b
                        owner
                        registrar
                        states
                        registrant_name
                        registrant_id
                        records {
                            content
                            id
                            name
                            ttl
                            type
                        }
                    }
                }
            `,
            {
                q: values.q,
                nameserver: values.nameserver,
            }
        )

        if (!error) {
            dns.value = data.dnsInfo
            openResultsModal.value = true
        }

        if (error) {
            const { graphQLErrors } = error
            const first = graphQLErrors[0]

            if (first?.extensions?.category === 'validation') {
                const validation = first.extensions.validation

                if (validation && validation.q) {
                    setFieldError('q', validation.q)
                }
            }
        }
    } finally {
        searching.value = false
    }
}
</script>
